/* global globalThis */
import React, { useEffect, useRef, useCallback } from "react";
import { useScannerState, useScanResultsState, useProtectionState, useLogsState } from "./PII/hooks";
import { ScannerControls, TabHeader, ActivityLogs, ProtectPIIButton, ScanResults } from "./PII/components";
import { ProtectionModal, ModeSelectionModal, GenerateScriptModal, DecryptScriptModal } from "./PII/modals";
import {
  fetchDataSourceId,
  fetchDatabasesList,
  fetchTablesList,
  fetchRegionsList,
  mapRegionsToCodes,
  fetchProtectionMethodsList,
  fetchScriptProtectionMethodsList,
  scanPIIData,
  applyProtectionToColumns,
  downloadProtectedDataFile,
  generateProtectionScript,
  fetchActivityLogs,
  generateDeanonymizationScript,
  getProtectionMessage,
} from "./PII/utils/apiService";
import "../upload/utils/Dashboard.css";

/**
 * PIIScanner - Main component for PII detection and protection
 * Refactored into smaller, composable components
 */
const PIIScanner = () => {
  const scannerState = useScannerState();
  const resultsState = useScanResultsState();
  const protectionState = useProtectionState();
  const logsState = useLogsState();

  // Refs for auto-scroll in Generate Script Modal
  const protectionCategoryRef = useRef(null);
  const protectionMethodRef = useRef(null);
  const scriptDownloadRef = useRef(null);

  // Initialize data source on mount
  useEffect(() => {
    const initializeDataSource = async () => {
      const configId = localStorage.getItem("organization_config_id");
      if (!configId) {
        logsState.setError("Please select an organization configuration from the header");
        return;
      }

      const fetchedDataSourceId = await fetchDataSourceId(configId);
      scannerState.setDataSourceId(fetchedDataSourceId);

      if (!fetchedDataSourceId) {
        logsState.setError("No data source found for the selected configuration");
      }
    };

    const handleStorageChange = () => {
      initializeDataSource();
      scannerState.resetSelections();
    };

    initializeDataSource();
    globalThis.addEventListener("localStorageUpdated", handleStorageChange);
    globalThis.addEventListener("customStorageChange", handleStorageChange);

    return () => {
      globalThis.removeEventListener("localStorageUpdated", handleStorageChange);
      globalThis.removeEventListener("customStorageChange", handleStorageChange);
    };
  }, []);

  // Fetch databases
  const handleFetchDatabases = useCallback(async () => {
    if (!scannerState.dataSourceId) return;

    scannerState.setLoadingDatabases(true);
    logsState.setError(null);

    try {
      const configId = localStorage.getItem("organization_config_id");
      const token = localStorage.getItem("token") || "";

      const databasesList = await fetchDatabasesList(
        scannerState.dataSourceId,
        configId,
        token,
      );
      scannerState.setDatabases(databasesList);

      if (databasesList.length === 0) {
        logsState.setError("No databases found for the selected configuration");
      }
    } catch (err) {
      logsState.setError(err.message);
      scannerState.setDatabases([]);
    } finally {
      scannerState.setLoadingDatabases(false);
    }
  }, [scannerState.dataSourceId]);

  // Fetch databases when dataSourceId changes (only on mount or when dataSourceId actually changes)
  useEffect(() => {
    if (scannerState.dataSourceId) {
      handleFetchDatabases();
    }
  }, [scannerState.dataSourceId]);

  // Fetch tables when database changes
  useEffect(() => {
    const fetchTableData = async () => {
      if (!scannerState.selectedDatabase || !scannerState.dataSourceId) {
        scannerState.setTables([]);
        return;
      }

      scannerState.setLoadingTables(true);
      logsState.setError(null);

      try {
        const configId = localStorage.getItem("organization_config_id");
        const token = localStorage.getItem("token") || "";

        const tablesList = await fetchTablesList(
          scannerState.dataSourceId,
          scannerState.selectedDatabase,
          configId,
          token,
        );

        scannerState.setTables(tablesList);

        if (tablesList.length === 0) {
          logsState.setError("No tables found for the selected database");
        }
      } catch (err) {
        logsState.setError(err.message);
        scannerState.setTables([]);
      } finally {
        scannerState.setLoadingTables(false);
      }
    };

    if (scannerState.selectedDatabase && scannerState.dataSourceId) {
      fetchTableData();
      scannerState.setSelectedTables([]);
      resultsState.setScanResults(null);
    } else {
      scannerState.setTables([]);
    }
  }, [scannerState.selectedDatabase, scannerState.dataSourceId]);

  // Fetch regions
  const handleFetchRegions = useCallback(async () => {
    scannerState.setLoadingRegions(true);

    try {
      const regionsList = await fetchRegionsList();
      const { finalRegions, displayMap } = mapRegionsToCodes(regionsList);

      scannerState.setRegions(finalRegions);
      scannerState.setRegionDisplayMap(displayMap);
      scannerState.setRegionsData(Array.isArray(regionsList) ? regionsList : regionsList.regions || []);
    } catch (err) {
      console.error("Error fetching regions:", err);
      scannerState.setRegions(["EU", "US", "UK", "CA", "AU", "JP", "BR", "SG", "IN", "CN"]);
    } finally {
      scannerState.setLoadingRegions(false);
    }
  }, []);

  // Initial load regions
  useEffect(() => {
    handleFetchRegions();
  }, [handleFetchRegions]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        scannerState.showDatabaseDropdown &&
        !event.target.closest(".database-dropdown-container")
      ) {
        scannerState.setShowDatabaseDropdown(false);
      }
      if (
        scannerState.showTableDropdown &&
        !event.target.closest(".table-dropdown-container")
      ) {
        scannerState.setShowTableDropdown(false);
      }
      if (
        scannerState.showRegionDropdown &&
        !event.target.closest(".region-dropdown-container")
      ) {
        scannerState.setShowRegionDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [
    scannerState.showDatabaseDropdown,
    scannerState.showTableDropdown,
    scannerState.showRegionDropdown,
  ]);

  // Fetch protection methods (only on mount)
  useEffect(() => {
    const fetchMethods = async () => {
      protectionState.setLoadingProtectionMethods(true);
      try {
        const methods = await fetchProtectionMethodsList();
        protectionState.setProtectionMethods(methods);
      } catch (err) {
        console.error("Error fetching protection methods:", err);
      } finally {
        protectionState.setLoadingProtectionMethods(false);
      }
    };

    fetchMethods();
  }, []);

  // Fetch script protection methods (only when script modal opens)
  useEffect(() => {
    const fetchScriptMethods = async () => {
      try {
        const methods = await fetchScriptProtectionMethodsList();
        protectionState.setScriptProtectionMethods(methods);
      } catch (err) {
        console.error("Error fetching script protection methods:", err);
      }
    };

    if (protectionState.showScriptModal) {
      fetchScriptMethods();
    }
  }, [protectionState.showScriptModal]);

  // Auto-scroll behaviors
  useEffect(() => {
    if (protectionState.selectedDbType && protectionCategoryRef.current) {
      setTimeout(() => {
        protectionCategoryRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [protectionState.selectedDbType]);

  useEffect(() => {
    if (protectionState.scriptProtectionCategory && protectionMethodRef.current) {
      setTimeout(() => {
        protectionMethodRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [protectionState.scriptProtectionCategory]);

  useEffect(() => {
    if (protectionState.scriptDownloadUrl && scriptDownloadRef.current) {
      setTimeout(() => {
        scriptDownloadRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [protectionState.scriptDownloadUrl]);

  // Handle analyze
  const handleAnalyze = async () => {
    if (!scannerState.selectedDatabase || scannerState.selectedTables.length === 0 || !scannerState.selectedRegion) {
      logsState.setError("Please select database, at least one table, and region");
      return;
    }

    if (!scannerState.dataSourceId) {
      logsState.setError("Data source ID not found. Please refresh and try again.");
      return;
    }

    resultsState.setLoading(true);
    logsState.setError(null);

    try {
      const configId = localStorage.getItem("organization_config_id");
      const token = localStorage.getItem("token") || "";

      const scanResult = await scanPIIData(
        scannerState.selectedDatabase,
        scannerState.selectedTables,
        scannerState.selectedRegion,
        scannerState.dataSourceId,
        configId,
        token,
      );

      resultsState.setScanResults(scanResult);

      // Auto-expand all tables
      if (scanResult?.results) {
        const initialExpanded = Object.fromEntries(
          scanResult.results.map((r) => [r.table_name, true]),
        );
        resultsState.setExpandedTables(initialExpanded);
      }
    } catch (err) {
      logsState.setError(err.message);
    } finally {
      resultsState.setLoading(false);
    }
  };

  // Handle protection
  const handleApplyProtection = async () => {
    if (!protectionState.selectedProtectionMethod) {
      logsState.setError("Please select a protection method");
      return;
    }

    const selectedColumns = Object.entries(resultsState.selectedPiiColumns)
      .filter(([, isSelected]) => isSelected)
      .map(([columnKey]) => {
        const [tableName, columnName] = columnKey.split("|||");
        return { table: tableName, column: columnName };
      });

    if (selectedColumns.length === 0) {
      logsState.setError("Please select at least one column to protect");
      return;
    }

    protectionState.setApplyingProtection(true);
    logsState.setError(null);

    try {
      const results = await applyProtectionToColumns({
        scanResults: resultsState.scanResults,
        selectedColumns: selectedColumns,
        protectionMethod: protectionState.selectedProtectionMethod,
        dataSourceId: scannerState.dataSourceId,
        database: scannerState.selectedDatabase,
        region: scannerState.selectedRegion,
        previewMode: protectionState.previewMode,
      });

      const hasFailures = results.some(
        (r) => r.data?.failed && r.data.failed.length > 0,
      );
      const hasSuccessfulResults = results.some(
        (r) => r.data?.results && r.data.results.length > 0,
      );

      if (hasFailures && !hasSuccessfulResults) {
        const errorMessages = results
          .map((r) => r.data?.failed?.map((f) => f.error).join(", "))
          .filter(Boolean)
          .join("; ");

        throw new Error(
          errorMessages || "Protection failed - session not found or expired",
        );
      }

      protectionState.setProtectionResult({
        success: hasSuccessfulResults,
        message: getProtectionMessage(hasFailures, protectionState.previewMode),
        results: results.flatMap((r) => r.data?.results || []).filter(Boolean),
        failed: results.flatMap((r) => r.data?.failed || []).filter(Boolean),
      });

      if (!protectionState.previewMode) {
        resultsState.setSelectedPiiColumns({});
        resultsState.setScanResults(null);
      }
    } catch (err) {
      logsState.setError("Failed to apply protection. Please try again.");
      console.error("Error applying protection:", err);
      protectionState.setProtectionResult({
        success: false,
        message: err.response?.data?.message || "Protection failed",
      });
    } finally {
      protectionState.setApplyingProtection(false);
    }
  };

  // Handle generate script
  const handleGenerateScript = async () => {
    if (!protectionState.selectedDbType || !protectionState.selectedScriptProtectionType) {
      logsState.setError("Please select DB type and protection type");
      return;
    }

    const sessionIds = resultsState.scanResults?.session_id
      ? [resultsState.scanResults.session_id]
      : [];

    protectionState.setGeneratingScript(true);
    protectionState.setScriptDownloadUrl(null);
    logsState.setError(null);

    try {
      const url = await generateProtectionScript(
        sessionIds,
        protectionState.selectedDbType,
        protectionState.scriptProtectionCategory,
        protectionState.selectedScriptProtectionType,
      );

      protectionState.setScriptDownloadUrl(url);
    } catch (err) {
      logsState.setError(err.message);
    } finally {
      protectionState.setGeneratingScript(false);
    }
  };

  // Handle fetch logs
  const handleFetchLogs = useCallback(async () => {
    logsState.setLoadingLogs(true);
    logsState.setLogsError(null);

    try {
      const logsData = await fetchActivityLogs();
      logsState.setLogs(logsData);
    } catch (err) {
      logsState.setLogsError(err.message);
    } finally {
      logsState.setLoadingLogs(false);
    }
  }, []);

  // Fetch logs when tab changes
  useEffect(() => {
    if (logsState.activeTab === "logs") {
      handleFetchLogs();
    }
  }, [logsState.activeTab, handleFetchLogs]);

  // Handle decrypt
  const handleGenerateDeanonymizationScript = async () => {
    if (!logsState.selectedLogForDecrypt) return;

    logsState.setDecryptingScript(true);
    logsState.setDecryptDownloadUrl(null);

    try {
      const url = await generateDeanonymizationScript(logsState.selectedLogForDecrypt.log_id);
      logsState.setDecryptDownloadUrl(url);
    } catch (err) {
      logsState.setError(err.message);
    } finally {
      logsState.setDecryptingScript(false);
    }
  };

  return (
    <div className="w-full space-y-6 p-6">
      {/* Tab Header */}
      <TabHeader
        activeTab={logsState.activeTab}
        setActiveTab={logsState.setActiveTab}
      />

      {logsState.activeTab === "scanner" ? (
        <>
          {/* Scanner Controls */}
          <ScannerControls
            databases={scannerState.databases}
            tables={scannerState.tables}
            regions={scannerState.regions}
            regionDisplayMap={scannerState.regionDisplayMap}
            regionsData={scannerState.regionsData}
            selectedDatabase={scannerState.selectedDatabase}
            selectedTables={scannerState.selectedTables}
            selectedRegion={scannerState.selectedRegion}
            loadingDatabases={scannerState.loadingDatabases}
            loadingTables={scannerState.loadingTables}
            loadingRegions={scannerState.loadingRegions}
            loading={resultsState.loading}
            error={logsState.error}
            showDatabaseDropdown={scannerState.showDatabaseDropdown}
            setShowDatabaseDropdown={scannerState.setShowDatabaseDropdown}
            showTableDropdown={scannerState.showTableDropdown}
            setShowTableDropdown={scannerState.setShowTableDropdown}
            showRegionDropdown={scannerState.showRegionDropdown}
            setShowRegionDropdown={scannerState.setShowRegionDropdown}
            onDatabaseSelect={(db) => {
              const dbName = typeof db === "string" ? db : db.name || db.database_name;
              scannerState.setSelectedDatabase(dbName);
            }}
            onTableSelect={(tableName) => {
              if (scannerState.selectedTables.includes(tableName)) {
                scannerState.setSelectedTables(
                  scannerState.selectedTables.filter((t) => t !== tableName),
                );
              } else {
                scannerState.setSelectedTables([...scannerState.selectedTables, tableName]);
              }
            }}
            onRegionSelect={scannerState.setSelectedRegion}
            onRefresh={() => {
              handleFetchDatabases();
              handleFetchRegions();
            }}
            onAnalyze={handleAnalyze}
            onSelectAllTables={(allTableNames) => {
              scannerState.setSelectedTables(allTableNames);
            }}
            onClearAllTables={() => {
              scannerState.setSelectedTables([]);
            }}
          />

          {/* Scan Results */}
          {resultsState.scanResults && (
            <ScanResults
              scanResults={resultsState.scanResults}
              expandedTables={resultsState.expandedTables}
              previewTables={resultsState.previewTables}
              distributionFilter={resultsState.distributionFilter}
              selectedPiiColumns={resultsState.selectedPiiColumns}
              selectedDatabase={scannerState.selectedDatabase}
              onToggleTable={(tableName) => {
                if (typeof tableName === "object") {
                  // Full object update from expand/collapse all
                  resultsState.setExpandedTables(tableName);
                } else {
                  // Toggle single table
                  resultsState.setExpandedTables((prev) => ({
                    ...prev,
                    [tableName]: !prev[tableName],
                  }));
                }
              }}
              onTogglePreview={(tableName) => {
                resultsState.setPreviewTables((prev) => ({
                  ...prev,
                  [tableName]: !prev[tableName],
                }));
              }}
              onSelectAllColumns={(tableName) => {
                if (resultsState.scanResults.summary) {
                  const targetResults = tableName
                    ? resultsState.scanResults.results.filter(
                        (r) => r.table_name === tableName
                      )
                    : resultsState.scanResults.results;

                  const newSelection = { ...resultsState.selectedPiiColumns };
                  targetResults.forEach((result) => {
                    result.pii_columns?.forEach((col) => {
                      const key = `${result.table_name}|||${col.column_name}`;
                      newSelection[key] = true;
                    });
                  });
                  resultsState.setSelectedPiiColumns(newSelection);
                } else {
                  resultsState.handleSelectAllColumns(tableName);
                }
              }}
              onDeselectAllColumns={(selection) => {
                if (selection) {
                  resultsState.setSelectedPiiColumns(selection);
                } else {
                  resultsState.handleDeselectAllColumns();
                }
              }}
              onColumnCheckboxChange={resultsState.handleColumnCheckboxChange}
              onSetDistributionFilter={resultsState.setDistributionFilter}
              regionsData={scannerState.regionsData}
            />
          )}

          {/* Protect PII Button */}
          {resultsState.getSelectedColumnsCount() > 0 && (
            <ProtectPIIButton
              selectedColumnsCount={resultsState.getSelectedColumnsCount()}
              selectedRegion={scannerState.selectedRegion}
              regionsData={scannerState.regionsData}
              onProtectClick={() => {
                protectionState.setShowModeSelectionModal(true);
              }}
            />
          )}
        </>
      ) : (
        /* Activity Logs Tab */
        <ActivityLogs
          logs={logsState.logs}
          isLoading={logsState.loadingLogs}
          error={logsState.logsError}
          onRefresh={handleFetchLogs}
          onDecryptClick={(log) => {
            logsState.setSelectedLogForDecrypt(log);
            logsState.setDecryptDownloadUrl(null);
            logsState.setShowDecryptModal(true);
          }}
        />
      )}

      {/* Modals */}
      <ModeSelectionModal
        isOpen={protectionState.showModeSelectionModal}
        selectedColumnsCount={resultsState.getSelectedColumnsCount()}
        onWebModeClick={() => {
          protectionState.setShowModeSelectionModal(false);
          protectionState.setShowProtectionModal(true);
        }}
        onScriptModeClick={() => {
          protectionState.setShowModeSelectionModal(false);
          protectionState.setShowScriptModal(true);
        }}
        onClose={() => {
          protectionState.setShowModeSelectionModal(false);
        }}
      />

      <ProtectionModal
        isOpen={protectionState.showProtectionModal}
        isLoading={protectionState.loadingProtectionMethods}
        selectedColumnsCount={resultsState.getSelectedColumnsCount()}
        selectedProtectionMethod={protectionState.selectedProtectionMethod}
        protectionMethods={protectionState.protectionMethods}
        protectionResult={protectionState.protectionResult}
        isApplying={protectionState.applyingProtection}
        previewMode={protectionState.previewMode}
        onMethodSelect={protectionState.setSelectedProtectionMethod}
        onApplyProtection={handleApplyProtection}
        onDownloadProtectedData={(result, idx) => {
          const sessionId =
            result.session_id ||
            result.data?.session_id ||
            result.id ||
            (typeof result === "string" ? result : null);

          if (sessionId) {
            downloadProtectedDataFile(sessionId, "csv");
          } else {
            logsState.setError("Session ID not found. Cannot download protected data.");
          }
        }}
        onClose={() => {
          protectionState.setShowProtectionModal(false);
          protectionState.setSelectedProtectionMethod("");
          protectionState.setProtectionResult(null);
        }}
      />

      <GenerateScriptModal
        isOpen={protectionState.showScriptModal}
        isGenerating={protectionState.generatingScript}
        selectedColumnsCount={resultsState.getSelectedColumnsCount()}
        selectedDbType={protectionState.selectedDbType}
        scriptProtectionCategory={protectionState.scriptProtectionCategory}
        selectedScriptProtectionType={protectionState.selectedScriptProtectionType}
        scriptProtectionMethods={protectionState.scriptProtectionMethods}
        scriptDownloadUrl={protectionState.scriptDownloadUrl}
        protectionCategoryRef={protectionCategoryRef}
        protectionMethodRef={protectionMethodRef}
        scriptDownloadRef={scriptDownloadRef}
        onDbTypeSelect={protectionState.setSelectedDbType}
        onProtectionCategorySelect={(category) => {
          protectionState.setScriptProtectionCategory(category);
          protectionState.setSelectedScriptProtectionType("");
        }}
        onMethodSelect={protectionState.setSelectedScriptProtectionType}
        onGenerateScript={handleGenerateScript}
        onClose={() => {
          protectionState.setShowScriptModal(false);
          protectionState.setScriptDownloadUrl(null);
          protectionState.setSelectedDbType("");
          protectionState.setSelectedScriptProtectionType("");
          protectionState.setScriptProtectionCategory("");
        }}
      />

      <DecryptScriptModal
        isOpen={logsState.showDecryptModal}
        isGenerating={logsState.decryptingScript}
        selectedLog={logsState.selectedLogForDecrypt}
        decryptDownloadUrl={logsState.decryptDownloadUrl}
        onGenerateDecryption={handleGenerateDeanonymizationScript}
        onClose={() => {
          logsState.setShowDecryptModal(false);
          logsState.setDecryptDownloadUrl(null);
          logsState.setSelectedLogForDecrypt(null);
        }}
      />
    </div>
  );
};

export default PIIScanner;
